/**
 * Wishing for a card from wherever you already are.
 *
 * The chase list used to be filled by walking to a picker: tap the plus in Wish,
 * choose a destination, and only then start hunting. That is backwards from how
 * it actually goes. You are browsing Explore, you see the card, and THEN you
 * think about where it belongs. The owner put it plainly: "If they are on
 * explore and go OH I REALLY WANT THAT CARD, they tap hold and wish for it in
 * the various ways."
 *
 * So this is not another picker. It is a mode that any grid of catalogue cards
 * can enter, holding the selection while the person keeps searching, sorting,
 * filtering and drilling into sets underneath it.
 *
 * THE ORDER OF THE QUESTIONS IS THE DESIGN
 *
 * The destination KIND is asked first, because it is the only thing that
 * changes the rules of selecting: the ten has a ceiling and a binder does not,
 * and once the kind is known the ceiling can stop you at the right moment
 * instead of arguing at the end. WHICH binder is asked last, because it changes
 * nothing while you pick, and because naming a new binder is far easier once
 * you are holding twelve cards than before you have any. The owner's rule,
 * which is a good one: choose early only what changes the rules.
 *
 * The card that was held is already selected when the mode opens. Holding a
 * card and landing on an empty selection would read as the app forgetting why
 * you were there.
 */
import { useCallback, useMemo, useState } from 'react';
import { X, Star, BookPlus, Check, Loader2 } from 'lucide-react';
import { createPortal } from 'react-dom';
import BottomSheet, { useHideNavWhile } from '../BottomSheet';
import haptics from '../../utils/haptics';
import {
  TOP_TEN_LIMIT, TOP_TEN_LABEL, addToTopTen, fetchTopTen,
} from '../../services/wishList';
import {
  fetchWishBinders, addCardsToWishBinder, createWishBinder,
  isDuplicateBinderNameError, duplicateBinderNameMessage,
} from '../../services/wishBinders';
import {
  emptySelection, toggleCard, selectedIdSet, selectedCards, refusalFor,
  isPickable, fitsAnother, capRefusal, roomNote, addedToTopTenMessage,
  addedMessage, TOP_TEN_DESTINATION_NAME,
} from './wishPickerSelection';

export const TOP_TEN = 'topTen';
export const BINDER = 'binder';

/**
 * The state a host screen needs to run this, kept here so a grid does not have
 * to know any of it. A host renders <WishFromExplore {...wish.ui} /> and hands
 * the grid `onCardHold={wish.onHold}` plus, while selecting, `selectedIds` and
 * a tap that toggles instead of opening.
 */
export function useWishFromExplore({ userId, showToast, enabled = true }) {
  const [held, setHeld] = useState(null);      // the card whose hold opened the popup
  const [kind, setKind] = useState(null);      // TOP_TEN | BINDER, null when not selecting
  const [selection, setSelection] = useState(emptySelection());
  const [freeSlots, setFreeSlots] = useState(null);
  // Set when the selection was started from INSIDE a binder, where the answer to
  // "which one" is already known. Started from the Wish menu it stays null and
  // the question is asked at the end, which is the owner's rule: choose early
  // only what changes the rules of selecting, and which binder changes none.
  const [presetBinder, setPresetBinder] = useState(null);
  // Bumped when somebody tries to leave Explore mid-selection. The bar shakes
  // rather than a dialog appearing, because the answer is "you are in the
  // middle of something", not a question worth stopping for.
  const [nudge, setNudge] = useState(0);

  const onHold = useCallback(async (card) => {
    if (!enabled || !userId) return;
    haptics.select();
    setHeld(card);
    // Read the ten's free slots while the popup is open rather than after a
    // choice, so the popup can say the ten is full instead of letting somebody
    // choose it and then refusing every tap.
    try {
      const rows = await fetchTopTen(userId);
      setFreeSlots(Math.max(0, TOP_TEN_LIMIT - (rows?.length || 0)));
    } catch {
      setFreeSlots(null);
    }
  }, [enabled, userId]);

  const destination = useMemo(() => {
    if (kind === TOP_TEN) {
      return { kind: TOP_TEN, name: TOP_TEN_DESTINATION_NAME, capacity: freeSlots ?? TOP_TEN_LIMIT };
    }
    if (kind === BINDER) return { kind: BINDER, name: 'a chase binder', capacity: null };
    return null;
  }, [kind, freeSlots]);

  const begin = useCallback((nextKind) => {
    haptics.tap();
    const first = held && isPickable(held) ? toggleCard(emptySelection(), held) : emptySelection();
    setKind(nextKind);
    setSelection(first);
    setHeld(null);
  }, [held]);

  /**
   * Arm the same selection mode from somewhere other than a hold - the plus in
   * the Top 10, or the plus inside a chase binder.
   *
   * There is no second picker screen. The owner said it plainly enough times:
   * "why aren't you just deputing to explore? ... Just take me to explore but
   * nothing selected - I keep repeating - REUSE EXPLORE with whatever explore
   * filters and sorts were on!" So this starts with an empty selection and
   * changes nothing about Explore: its sort, its filters and its zoom are
   * whatever they already were.
   */
  const beginFor = useCallback(async (nextKind, { binder = null } = {}) => {
    haptics.tap();
    setPresetBinder(binder);
    setSelection(emptySelection());
    setKind(nextKind);
    if (nextKind === TOP_TEN && userId) {
      try {
        const rows = await fetchTopTen(userId);
        setFreeSlots(Math.max(0, TOP_TEN_LIMIT - (rows?.length || 0)));
      } catch { setFreeSlots(null); }
    }
  }, [userId]);

  const cancel = useCallback(() => {
    setKind(null);
    setPresetBinder(null);
    setSelection(emptySelection());
    setHeld(null);
  }, []);

  const onToggle = useCallback((card) => {
    const already = selection.has(card.id);
    if (!already) {
      const refusal = refusalFor(card);
      if (refusal) { showToast?.(refusal); return; }
      if (!fitsAnother(selection, destination)) { showToast?.(capRefusal(destination)); return; }
    }
    haptics.select();
    setSelection((prev) => toggleCard(prev, card));
  }, [selection, destination, showToast]);

  return {
    selecting: kind !== null,
    kind,
    destination,
    selection,
    selectedIds: kind !== null ? selectedIdSet(selection) : null,
    onHold,
    onToggle,
    beginFor,
    /** Somebody tried to go somewhere selection cannot follow. */
    bump: () => { haptics.warn(); setNudge((n) => n + 1); },
    cancel,
    ui: {
      userId,
      held,
      kind,
      destination,
      selection,
      freeSlots,
      presetBinder,
      nudge,
      showToast,
      onChoose: begin,
      onDismissPopup: () => setHeld(null),
      onCancel: cancel,
      onDone: cancel,
    },
  };
}

/** The popup and the docked bar. Rendered by the host, driven by the hook. */
export default function WishFromExplore({
  userId, held, kind, destination, selection, freeSlots, presetBinder, nudge, showToast,
  onChoose, onDismissPopup, onCancel, onDone, theme,
}) {
  const t = theme;
  const [binderOpen, setBinderOpen] = useState(false);
  const [binders, setBinders] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState('');
  const count = selection?.size || 0;

  // The nav steps aside for the whole selection, not just for the sheets. You
  // can move anywhere INSIDE Explore while picking - back to the sets, back to
  // the eras - but Home, Shops and Chat are not somewhere a half-finished
  // selection can follow you to.
  useHideNavWhile(!!held || binderOpen || kind !== null);

  const openBinderChooser = async () => {
    haptics.tap();
    setBinderOpen(true);
    setError('');
    try { setBinders(await fetchWishBinders(userId)); } catch { setBinders([]); }
  };

  const commitTopTen = async () => {
    setBusy(true);
    const tally = { added: 0, full: 0, failed: 0 };
    for (const card of selectedCards(selection)) {
      // Sequential on purpose: the slot and the cap are the database's, and ten
      // parallel inserts would race each other for the last square.
      // eslint-disable-next-line no-await-in-loop
      const result = await addToTopTen(userId, {
        tcgdexCardId: card.id,
        cardName: card.name,
        setId: card.set_id,
        setName: card.set_name,
        imageUrl: card.image_url,
      });
      if (result.added) tally.added += 1;
      else if (result.full) tally.full += 1;
      else tally.failed += 1;
    }
    setBusy(false);
    showToast?.(addedToTopTenMessage(tally));
    onDone();
  };

  const commitBinder = async (binder) => {
    setBusy(true);
    try {
      const result = await addCardsToWishBinder(userId, binder.id, selectedCards(selection));
      showToast?.(addedMessage({ added: result?.added ?? count, already: result?.already ?? 0 }));
      setBinderOpen(false);
      onDone();
    } catch (e) {
      setError(e?.message || 'Those cards could not be added.');
    }
    setBusy(false);
  };

  const commitNewBinder = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    setError('');
    try {
      const binder = await createWishBinder(userId, { name });
      setNewName('');
      await commitBinder(binder);
    } catch (e) {
      setError(isDuplicateBinderNameError(e) ? duplicateBinderNameMessage(name) : (e?.message || 'That binder could not be made.'));
      setBusy(false);
    }
  };

  const note = destination ? roomNote(selection, destination) : null;

  return (
    <>
      {/* The destination popup. Front and centre, not a corner menu: it is the
          one decision that sets the rules for everything that follows. */}
      <BottomSheet
        open={!!held}
        onClose={onDismissPopup}
        label="Wish for this card"
        overlayStyle={styles.overlay}
        sheetStyle={{ ...styles.sheet, backgroundColor: t.bgSheet }}
      >
        <div style={styles.handle} />
        <h2 style={{ ...styles.title, color: t.text }}>{held?.name}</h2>
        <p style={{ ...styles.sub, color: t.textSecondary }}>Where should this go?</p>

        <button
          style={{ ...styles.choice, backgroundColor: t.bgCard, borderColor: t.border }}
          onClick={() => onChoose(TOP_TEN)}
          disabled={freeSlots === 0}
        >
          <Star size={18} color={freeSlots === 0 ? t.textMuted : t.warning} />
          <span style={{ ...styles.choiceName, color: freeSlots === 0 ? t.textMuted : t.text }}>
            {TOP_TEN_LABEL}
          </span>
          <span style={{ ...styles.choiceMeta, color: t.textMuted }}>
            {freeSlots === 0 ? 'full' : freeSlots === null ? '' : `${freeSlots} free`}
          </span>
        </button>

        <button
          style={{ ...styles.choice, backgroundColor: t.bgCard, borderColor: t.border }}
          onClick={() => onChoose(BINDER)}
        >
          <BookPlus size={18} color={t.accent} />
          <span style={{ ...styles.choiceName, color: t.text }}>A chase binder</span>
          <span style={{ ...styles.choiceMeta, color: t.textMuted }}>pick or make one after</span>
        </button>
      </BottomSheet>

      {/* The docked bar, on the body for the same reason the picker's is: inside
          the page scroller iOS WebKit treats a fixed element as absolute and it
          never clears the nav. No scrim, because its whole job is to sit there
          while you keep browsing. */}
      {kind !== null && !binderOpen && typeof document !== 'undefined' && createPortal(
        <div
          key={nudge}
          style={{
            ...styles.bar,
            backgroundColor: t.bgSheet,
            borderTop: `1px solid ${t.border}`,
            ...(nudge > 0 ? { animation: 'cc-nudge 0.4s ease' } : null),
          }}
        >
          <button style={{ ...styles.cancel, color: t.textSecondary }} onClick={onCancel} disabled={busy}>
            <X size={18} />
          </button>
          <div style={styles.barText}>
            <span style={{ ...styles.barCount, color: t.text }}>
              {count === 0 ? 'Pick the cards you want' : `${count} selected`}
            </span>
            {note && <span style={{ ...styles.barNote, color: t.textMuted }}>{note}</span>}
          </div>
          <button
            style={{
              ...styles.go,
              backgroundColor: count === 0 ? t.bgCard : t.warning,
              color: count === 0 ? t.textMuted : '#000',
            }}
            disabled={count === 0 || busy}
            onClick={() => {
              if (kind === TOP_TEN) { commitTopTen(); return; }
              // Started from inside a binder, so which one was never in doubt.
              if (presetBinder) { commitBinder(presetBinder); return; }
              openBinderChooser();
            }}
          >
            {busy ? <Loader2 size={16} style={styles.spin} /> : <Check size={16} />}
            {kind === TOP_TEN || presetBinder ? 'Add' : 'Choose binder'}
          </button>
        </div>,
        document.body,
      )}

      {/* Which binder, asked last. Nothing above this point depended on it. */}
      <BottomSheet
        open={binderOpen}
        onClose={busy ? () => {} : () => setBinderOpen(false)}
        label="Which chase binder"
        overlayStyle={styles.overlay}
        sheetStyle={{ ...styles.sheet, backgroundColor: t.bgSheet }}
      >
        <div style={styles.handle} />
        <h2 style={{ ...styles.title, color: t.text }}>
          Where do these {count} {count === 1 ? 'card' : 'cards'} go?
        </h2>
        {error && <p style={{ ...styles.error, color: t.error }}>{error}</p>}

        {binders === null && (
          <div style={styles.loading}>
            <Loader2 size={18} color={t.textMuted} style={styles.spin} />
          </div>
        )}

        {binders !== null && binders.length === 0 && (
          <p style={{ ...styles.sub, color: t.textSecondary }}>
            You have no chase binders yet. Name your first one below.
          </p>
        )}

        {(binders || []).map((b) => (
          <button
            key={b.id}
            style={{ ...styles.choice, backgroundColor: t.bgCard, borderColor: t.border }}
            onClick={() => commitBinder(b)}
            disabled={busy}
          >
            <span style={{ ...styles.choiceName, color: t.text }}>{b.name}</span>
          </button>
        ))}

        <div style={styles.newRow}>
          <input
            style={{ ...styles.input, backgroundColor: t.bgCard, borderColor: t.border, color: t.text }}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="or name a new one"
            disabled={busy}
          />
          <button
            style={{
              ...styles.newGo,
              backgroundColor: newName.trim() ? t.warning : t.bgCard,
              color: newName.trim() ? '#000' : t.textMuted,
            }}
            onClick={commitNewBinder}
            disabled={busy || !newName.trim()}
          >
            Make it
          </button>
        </div>
      </BottomSheet>
    </>
  );
}

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10004,
  },
  sheet: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10005,
    borderRadius: '20px 20px 0 0',
    padding: '8px 16px calc(20px + var(--safe-area-bottom, 0px))',
    maxHeight: '80vh', overflowY: 'auto',
  },
  handle: {
    width: '36px', height: '4px', borderRadius: '2px',
    backgroundColor: 'rgba(255,255,255,0.25)', margin: '4px auto 12px',
  },
  title: { fontSize: '18px', fontWeight: '700', margin: '0 0 4px' },
  sub: { fontSize: '13px', margin: '0 0 14px' },
  choice: {
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    padding: '14px 14px', marginBottom: '8px',
    border: '1px solid transparent', borderRadius: '14px',
    cursor: 'pointer', textAlign: 'left', minHeight: '44px',
  },
  choiceName: { flex: 1, fontSize: '15px', fontWeight: '600' },
  choiceMeta: { fontSize: '12px' },
  bar: {
    position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 10002,
    display: 'flex', alignItems: 'center', gap: '10px',
    padding: '10px 12px calc(14px + var(--safe-area-bottom, 0px))',
  },
  cancel: {
    width: '44px', height: '44px', display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: 'transparent', border: 'none',
    cursor: 'pointer', flexShrink: 0,
  },
  barText: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  barCount: { fontSize: '14px', fontWeight: '600' },
  barNote: { fontSize: '11px' },
  go: {
    display: 'flex', alignItems: 'center', gap: '6px',
    padding: '0 16px', height: '44px', borderRadius: '12px',
    border: 'none', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    flexShrink: 0,
  },
  newRow: { display: 'flex', gap: '8px', marginTop: '10px' },
  input: {
    flex: 1, height: '44px', padding: '0 12px', borderRadius: '12px',
    border: '1px solid transparent', fontSize: '15px', outline: 'none',
  },
  newGo: {
    height: '44px', padding: '0 14px', borderRadius: '12px', border: 'none',
    fontSize: '14px', fontWeight: '700', cursor: 'pointer',
  },
  error: { fontSize: '13px', margin: '0 0 10px' },
  loading: { display: 'flex', justifyContent: 'center', padding: '16px' },
  spin: { animation: 'cc-spin 1s linear infinite' },
};
